import { useState, useEffect, useRef } from 'react'
import { Menu, Bell, User, AlertTriangle, CheckCircle, UserPlus, TrendingDown, UserX, ClipboardList, X, Target, Users, LogOut, Home } from 'lucide-react'
import { api } from '../../services/api'
import { useAuthStore } from '../../store/authStore'
import styles from './Header.module.css'

interface Notificacao {
  id: number
  tipo: string
  prioridade: string
  titulo: string
  mensagem: string
  icone: string
  cor: string
  link: string
}

interface NotificacoesData {
  total: number
  nao_lidas: number
  notificacoes: Notificacao[]
}

interface HeaderProps {
  onMenuClick: () => void
}

const HUB_URL = import.meta.env.VITE_HUB_URL || 'https://hub.grupofaj.com.br'

export function Header({ onMenuClick }: HeaderProps) {
  const { user, logout } = useAuthStore()
  const [notificacoes, setNotificacoes] = useState<NotificacoesData | null>(null)
  const [showDropdown, setShowDropdown] = useState(false)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const userMenuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    async function loadNotificacoes() {
      try {
        const data = await api.getNotificacoes()
        setNotificacoes(data)
      } catch (err) {
        console.error('Erro ao carregar notificações:', err)
      }
    }
    loadNotificacoes()

    // Atualiza a cada 5 minutos
    const interval = setInterval(loadNotificacoes, 5 * 60 * 1000)
    return () => clearInterval(interval)
  }, [])

  // Fecha dropdowns ao clicar fora
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false)
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const getIcon = (icone: string, cor: string) => {
    const size = 18
    const style = { color: cor }
    switch (icone) {
      case 'alert-triangle':
        return <AlertTriangle size={size} style={style} />
      case 'check-circle':
        return <CheckCircle size={size} style={style} />
      case 'user-plus':
        return <UserPlus size={size} style={style} />
      case 'trending-down':
        return <TrendingDown size={size} style={style} />
      case 'user-x':
        return <UserX size={size} style={style} />
      case 'clipboard-list':
        return <ClipboardList size={size} style={style} />
      case 'target':
        return <Target size={size} style={style} />
      case 'users':
        return <Users size={size} style={style} />
      default:
        return <Bell size={size} style={style} />
    }
  }

  const handleNotificationClick = (link: string) => {
    setShowDropdown(false)
    window.location.href = link
  }

  return (
    <header className={styles.header}>
      {/* Left Side */}
      <div className={styles.left}>
        <button className={styles.menuButton} onClick={onMenuClick} aria-label="Menu">
          <Menu size={24} />
        </button>
      </div>

      {/* Right Side */}
      <div className={styles.right}>
        {/* Notifications */}
        <div className={styles.notificationWrapper} ref={dropdownRef}>
          <button
            className={styles.iconButton}
            aria-label="Notificações"
            onClick={() => setShowDropdown(!showDropdown)}
          >
            <Bell size={20} />
            {notificacoes && notificacoes.total > 0 && (
              <span className={styles.notificationBadge}>{notificacoes.total}</span>
            )}
          </button>

          {/* Dropdown */}
          {showDropdown && (
            <div className={styles.notificationDropdown}>
              <div className={styles.notificationHeader}>
                <h3>Notificações</h3>
                <button
                  className={styles.closeDropdown}
                  onClick={() => setShowDropdown(false)}
                >
                  <X size={18} />
                </button>
              </div>

              <div className={styles.notificationList}>
                {notificacoes && notificacoes.notificacoes.length > 0 ? (
                  notificacoes.notificacoes.map((notif) => (
                    <div
                      key={notif.id}
                      className={`${styles.notificationItem} ${styles[notif.prioridade]}`}
                      onClick={() => handleNotificationClick(notif.link)}
                    >
                      <div className={styles.notificationIcon}>
                        {getIcon(notif.icone, notif.cor)}
                      </div>
                      <div className={styles.notificationContent}>
                        <span className={styles.notificationTitle}>{notif.titulo}</span>
                        <span className={styles.notificationMessage}>{notif.mensagem}</span>
                      </div>
                      <div
                        className={styles.notificationIndicator}
                        style={{ backgroundColor: notif.cor }}
                      />
                    </div>
                  ))
                ) : (
                  <div className={styles.notificationEmpty}>
                    <CheckCircle size={32} />
                    <p>Nenhuma notificação</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* User Menu */}
        <div className={styles.userWrapper} ref={userMenuRef}>
          <button
            className={styles.user}
            onClick={() => setShowUserMenu(!showUserMenu)}
          >
            <div className={styles.userAvatar}>
              <User size={20} />
            </div>
            <div className={styles.userInfo}>
              <span className={styles.userName}>{user?.nome || 'Usuário'}</span>
              <span className={styles.userRole}>{user?.perfil || 'Carregando...'}</span>
            </div>
          </button>

          {/* User Dropdown Menu */}
          {showUserMenu && (
            <div className={styles.userDropdown}>
              <div className={styles.userDropdownHeader}>
                <span className={styles.userDropdownName}>{user?.nome}</span>
                <span className={styles.userDropdownEmail}>{user?.email}</span>
              </div>
              <div className={styles.userDropdownDivider} />
              <button
                className={styles.userDropdownItem}
                onClick={() => {
                  setShowUserMenu(false)
                  window.location.href = HUB_URL
                }}
              >
                <Home size={16} />
                <span>Voltar ao Hub</span>
              </button>
              <button
                className={styles.userDropdownItem}
                onClick={() => {
                  setShowUserMenu(false)
                  logout()
                }}
              >
                <LogOut size={16} />
                <span>Sair</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}
